import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { PlatformPage } from "@/features/platform/components/platform-chrome";
import { ClientView } from "@/features/platform/modules/client-view/client-view";
import { PlatformClosing } from "@/features/platform/modules/closing/platform-closing";
import { DayDigest } from "@/features/platform/modules/digest/day-digest";
import { PlatformHero } from "@/features/platform/modules/hero/platform-hero";
import { PassesBand } from "@/features/platform/modules/passes/passes-band";
import { PlanBuilder } from "@/features/platform/modules/plan-builder/plan-builder";
import { PlatformPreview } from "@/features/platform/modules/preview/platform-preview";
import { PlatformPricing } from "@/features/platform/modules/pricing/platform-pricing";
import { PlatformQuestions } from "@/features/platform/modules/questions/platform-questions";
import { PlatformSteps } from "@/features/platform/modules/steps/platform-steps";
import { TeamSize } from "@/features/platform/modules/team/team-size";
import { ToolsRail } from "@/features/platform/modules/tools-rail/tools-rail";
import { jsonLdScript, organizationJsonLd, pageMeta, SITE_NAME, websiteJsonLd } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { getUrlOrigin } from "@/paraglide/runtime.js";

export const Route = createFileRoute("/(public)/")({
  head: () => ({
    meta: pageMeta({ title: m.seo_home_title(), description: m.app_description() }),
    scripts: [
      organizationJsonLd(),
      websiteJsonLd(),
      // This site sells the software itself, so it says so.
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: SITE_NAME,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: `${getUrlOrigin()}/`,
        description: m.app_description(),
      }),
    ],
  }),
  component: PlatformLanding,
  loader: () => ({ crumb: m.landing_crumb() }),
  // A sign-in that bounced off a private page lands here with where it was going.
  validateSearch: z.object({ redirect: z.string().optional() }),
});

/**
 * The platform's front page, for the trainer deciding whether to run their business here. One
 * point per section, each laid out its own way: the day on an arc of light, the six tools, the
 * schedule, how to start, the plan editor, passes, a team of any size, what the client sees, the
 * morning email, then the price list and the questions trainers ask before they move.
 *
 * A trainer's own page is /<slug>; this one never shows a real calendar, only the shape of one.
 */
function PlatformLanding() {
  return (
    <PlatformPage head={<PlatformHero />}>
      <ToolsRail />
      <PlatformPreview />
      <PlatformSteps />
      <PlanBuilder />
      <PassesBand />
      <TeamSize />
      <ClientView />
      <DayDigest />
      <PlatformPricing />
      <PlatformQuestions />
      <PlatformClosing />
    </PlatformPage>
  );
}
